import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDownload,
  faFile,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFilePowerpoint,
  faFileText,
  faSearch,
  faFilter,
  faSpinner,
  faExclamationTriangle,
  faCloudDownloadAlt,
  faTags,
  faCalendarAlt,
  faEye
} from '@fortawesome/free-solid-svg-icons';
import { getAllFreeDownloads, getFreeDownloadCategories, downloadFreeDownload } from '../../services/freeDownloads.util';

const FreeDownloads = () => {
  const [downloads, setDownloads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [downloadingIds, setDownloadingIds] = useState(new Set());

  // Fetch downloads and categories on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch downloads and categories in parallel
        const [downloadsResponse, categoriesResponse] = await Promise.all([
          getAllFreeDownloads(),
          getFreeDownloadCategories()
        ]);

        if (downloadsResponse.success) {
          setDownloads(downloadsResponse.data || []);
        }

        if (categoriesResponse.success) {
          setCategories(categoriesResponse.data || []);
        }
      } catch (err) {
        console.error('Error loading free downloads:', err);
        setError(err.message || 'Failed to load free downloads');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter downloads based on search term and category
  const filteredDownloads = downloads.filter(download => {
    const matchesSearch = download.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         download.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         download.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || download.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Get file icon based on file type
  const getFileIcon = (fileType) => {
    const iconMap = {
      'pdf': faFilePdf,
      'doc': faFileWord,
      'docx': faFileWord,
      'xls': faFileExcel,
      'xlsx': faFileExcel,
      'ppt': faFilePowerpoint,
      'pptx': faFilePowerpoint,
      'txt': faFileText
    };
    return iconMap[fileType] || faFile;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / Math.pow(1024, i) * 100) / 100} ${sizes[i]}`;
  };

  // Handle download
  const handleDownload = async (download) => {
    try {
      setDownloadingIds(prev => new Set([...prev, download._id]));
      
      // Get download URL and redirect
      const downloadUrl = downloadFreeDownload(download._id);
      window.open(downloadUrl, '_blank');
      
      // Small delay to show download feedback
      setTimeout(() => {
        setDownloadingIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(download._id);
          return newSet;
        });
      }, 2000);
      
    } catch (err) {
      console.error('Download error:', err);
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(download._id);
        return newSet;
      });
    }
  };

  return (
    <div className="free-downloads-container">
      <style>{`
        .free-downloads-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #000000 0%, #1a1a1a 100%);
          color: white;
          position: relative;
        }
        
        .free-downloads-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICAgIDxnIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjAzKSIgZmlsbC1ydWxlPSJub256ZXJvIj4KICAgICAgICAgICAgPGNpcmNsZSBjeD0iNyIgY3k9IjciIHI9IjciLz4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==');
          opacity: 0.4;
          pointer-events: none;
        }
        
        .content-wrapper {
          position: relative;
          z-index: 1;
        }
        
        .page-header {
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          padding: 60px 0 40px 0;
        }
        
        .page-header__title {
          color: white !important;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          font-size: 48px !important;
          font-weight: 700 !important;
          margin-bottom: 16px !important;
        }
        
        .page-header__subtitle {
          color: rgba(255, 255, 255, 0.8) !important;
          font-size: 18px !important;
          line-height: 1.6;
        }
        
        .filters-section {
          background: rgba(0, 0, 0, 0.3);
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 40px;
        }
        
        .search-box {
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          padding: 12px 16px;
          color: white;
          font-size: 16px;
          transition: all 0.3s ease;
          width: 100%;
        }
        
        .search-box:focus {
          outline: none;
          border-color: rgba(255, 255, 255, 0.4);
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
          background: rgba(0, 0, 0, 0.8);
        }
        
        .search-box::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }
        
        .category-filter {
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          padding: 12px 16px;
          color: white;
          font-size: 16px;
          transition: all 0.3s ease;
          width: 100%;
        }
        
        .category-filter:focus {
          outline: none;
          border-color: rgba(255, 255, 255, 0.4);
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
          background: rgba(0, 0, 0, 0.8);
        }
        
        .category-filter option {
          background: #1a1a1a;
          color: white;
        }
        
        .download-card {
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(20, 20, 20, 0.7) 100%);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.15);
          border-radius: 20px;
          padding: 24px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          cursor: pointer;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        
        .download-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
        }
        
        .download-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5), 0 0 30px rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.3);
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(30, 30, 30, 0.8) 100%);
        }
        
        .file-icon {
          width: 64px;
          height: 64px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          color: #4A90E2;
          font-size: 28px;
          transition: all 0.3s ease;
        }
        
        .download-card:hover .file-icon {
          background: rgba(255, 255, 255, 0.15);
          transform: scale(1.05);
          color: #5BA0F2;
        }
        
        .file-title {
          font-size: 18px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 8px;
          line-height: 1.3;
        }
        
        .file-description {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.5;
          margin-bottom: 16px;
          flex-grow: 1;
        }
        
        .file-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
        }
        
        .file-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-bottom: 16px;
        }
        
        .file-tag {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 4px 8px;
          font-size: 10px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .download-button {
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(15, 15, 15, 0.7) 50%, rgba(0, 0, 0, 0.8) 100%);
          color: #ffffff;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          padding: 12px 20px;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          backdrop-filter: blur(15px);
          -webkit-backdrop-filter: blur(15px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
          text-decoration: none;
          width: 100%;
          margin-top: auto;
        }
        
        .download-button:hover {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(0, 0, 0, 0.9) 50%, rgba(255, 255, 255, 0.1) 100%);
          color: #ffffff;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.5);
          text-decoration: none;
        }
        
        .download-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
        }
        
        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          color: rgba(255, 255, 255, 0.8);
          text-align: center;
        }
        
        .stats-bar {
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
        }
        
        .downloads-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 24px;
        }
        
        @media (max-width: 768px) {
          .page-header__title {
            font-size: 36px !important;
          }
          
          .downloads-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          
          .download-card {
            padding: 20px;
          }
          
          .filters-section {
            padding: 20px;
          }
        }
      `}</style>
      
      <div className="content-wrapper">
        {/* Page Header */}
        <section className="page-header">
          <div className="container">
            <div className="row justify-center text-center">
              <div className="col-xl-8 col-lg-10">
                <h1 className="page-header__title">
                  <FontAwesomeIcon icon={faCloudDownloadAlt} className="me-3" />
                  Free Downloads
                </h1>
                <p className="page-header__subtitle">
                  Access our collection of free educational resources, study materials, and reference documents. 
                  Download instantly - no registration required.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="layout-pt-md layout-pb-md">
          <div className="container">
            {/* Filters Section */}
            {!loading && !error && (
              <div className="filters-section">
                <div className="row align-items-center">
                  <div className="col-md-8 mb-3 mb-md-0">
                    <div className="position-relative">
                      <FontAwesomeIcon 
                        icon={faSearch} 
                        className="position-absolute start-0 top-50 translate-middle-y ms-3 text-white-50"
                      />
                      <input
                        type="text"
                        className="search-box ps-5"
                        placeholder="Search downloads by title, description, or tags..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="position-relative">
                      <FontAwesomeIcon 
                        icon={faFilter} 
                        className="position-absolute start-0 top-50 translate-middle-y ms-3 text-white-50"
                      />
                      <select
                        className="category-filter ps-5"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        <option value="all">All Categories</option>
                        {categories.map(category => (
                          <option key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Bar */}
            {!loading && !error && downloads.length > 0 && (
              <div className="stats-bar">
                <div>
                  <FontAwesomeIcon icon={faFile} className="me-2" />
                  Showing {filteredDownloads.length} of {downloads.length} downloads
                </div>
                <div>
                  <FontAwesomeIcon icon={faTags} className="me-2" />
                  {categories.length} categories available
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="loading-state">
                <FontAwesomeIcon icon={faSpinner} spin size="3x" className="mb-3" />
                <h5 className="mb-2">Loading Downloads...</h5>
                <p>Please wait while we fetch the available downloads</p>
              </div>
            )}

            {/* Error State */}
            {!loading && error && (
              <div className="error-state">
                <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="mb-3 text-warning" />
                <h5 className="mb-2">Error Loading Downloads</h5>
                <p className="mb-4">{error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="download-button"
                  style={{ width: 'auto', padding: '12px 24px' }}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Downloads Grid */}
            {!loading && !error && filteredDownloads.length > 0 && (
              <div className="downloads-grid">
                {filteredDownloads.map((download) => (
                  <div key={download._id} className="download-card">
                    <div className="file-icon">
                      <FontAwesomeIcon icon={getFileIcon(download.fileType)} />
                    </div>
                    
                    <h3 className="file-title">{download.title}</h3>
                    
                    {download.description && (
                      <p className="file-description">{download.description}</p>
                    )}
                    
                    <div className="file-meta">
                      <span>
                        <FontAwesomeIcon icon={faFile} className="me-1" />
                        {download.fileType?.toUpperCase() || 'FILE'}
                      </span>
                      <span>
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        {download.downloadCount || 0} downloads
                      </span>
                      <span>{formatFileSize(download.fileSize)}</span>
                    </div>
                    
                    {download.tags && download.tags.length > 0 && (
                      <div className="file-tags">
                        {download.tags.map((tag, index) => (
                          <span key={index} className="file-tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <button
                      className="download-button"
                      onClick={() => handleDownload(download)}
                      disabled={downloadingIds.has(download._id)}
                    >
                      {downloadingIds.has(download._id) ? (
                        <>
                          <FontAwesomeIcon icon={faSpinner} spin />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faDownload} />
                          Download
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredDownloads.length === 0 && downloads.length > 0 && (
              <div className="empty-state">
                <FontAwesomeIcon icon={faSearch} size="3x" className="mb-3 text-white-50" />
                <h5 className="mb-2">No downloads found</h5>
                <p>Try adjusting your search criteria or browse different categories</p>
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                  className="download-button"
                  style={{ width: 'auto', padding: '12px 24px', marginTop: '16px' }}
                >
                  Clear Filters
                </button>
              </div>
            )}

            {/* No Downloads Available */}
            {!loading && !error && downloads.length === 0 && (
              <div className="empty-state">
                <FontAwesomeIcon icon={faCloudDownloadAlt} size="3x" className="mb-3 text-white-50" />
                <h5 className="mb-2">No downloads available</h5>
                <p>There are currently no free downloads available. Please check back later.</p>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default FreeDownloads;
